import React, { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { RichText } from 'src/components/RichText/RichText';
import { MediaEntry, ProductItemBySku, ProductPrice, ProductType, MediaGalleryEntry } from 'src/types/global-types';
import { useVatCausesRender } from 'src/components/AppShell/VatProvider';
import { Price, StyledText } from 'src/components/Helpers/Price';
import { PriceBadge } from 'src/components/ProductDetail/PriceBadge';
import classnames from 'classnames';
import { DeliveryGuideSidebar } from 'src/components/DeliveryGuideSidebar/DeliveryGuideSidebar';
import CustomisationSidebar from 'src/components/CustomisationSidebar/CustomisationSidebar';
import { SizeGuideLoader } from 'src/components/SizeGuide/SizeGuideLoader';
import { ProductSidebars } from 'src/components/ProductDetail/ProductSidebars';
import { ProductSidebar } from 'src/components/ProductDetail/ProductSidebar';
import { ApplicationMethod, ProductLocation } from 'src/components/CustomisationPage/feature/customisation.types';
import { ProductLocations } from 'src/components/ProductDetail/ProductLocations';
import { DeliveryTab } from 'src/components/ProductDetail/DeliveryTab';
import { useBreakpointMatch, useRenderEnv } from 'src/hooks/useBreakpoint';
import { QuickQuote } from 'src/elements/QuickQuote/QuickQuote';
import { Tabs } from 'src/components/Tabs/Tabs';
import { Product } from 'src/components/ProductDetail/utils/product-detail.types';
import Bugsnag from '@bugsnag/js';
import { getPercentage } from 'src/helpers/getPercentage';
import { CATEGORIES_TO_DISPLAY_DPD_AND_RETURNS_BADGES, MIN_PRICE_TO_FREE_FEDEX_DELIVERY } from 'src/constants/product';
import { Breadcrumbs } from 'src/components/Breadcrumbs/Breadcrumbs';
import { iconsSmallWidth } from 'src/components/ProductDetail/utils/iconsSmallWidth';
import { getUrl } from 'src/components/ProductDetail/utils/getUrl';
import { ProductDetailRelatedProducts } from 'src/components/ProductDetail/ProductDetailRelatedProducts';
import camelcaseKeys from 'camelcase-keys';
import { useQuery } from '@apollo/client';
import categoryIdByName from 'src/queries/categoryIdByName.graphql';
import { BESTSELLERS_CATEGORY, EXCLUDED_PRODUCTS_CATEGORY } from 'src/constants/product';
import { YotpoReviews } from 'src/components/YotpoReviews/YotpoReviews';
import { YotpoRating } from 'src/components/YotpoReviews/YotpoRating';
import { useAggregationsByCategoryId } from 'src/components/CategoryListingPage/AggregationQueryProvider';
import { useIsIrishUser } from 'src/util/cookies';

import { ModalProductInfo } from './ModalProductInfo';
import { ProductInfo } from './ProductInfo';
import { ProductGallery, ProductGalleryProvider } from './ProductGallery';
import { BaseVariant, BaseProduct, PreviousPrice } from './utils/product-detail.types';
import { ProductConfigOptions } from './ProductOptions';
import classes from './ProductDetail.scss';
import { RelatedCategories } from './RelatedCategories/RelatedCategories';

type Props = {
    product: BaseProduct;
    price: ProductPrice | null;
    priceRender?: React.ReactNode;
    showSku: boolean;
    showPrice?: boolean;
    showTabs?: boolean;
    showReview?: boolean;
    media_entries: MediaEntry[];
    productVideo?: MediaGalleryEntry[];
    options?: ProductConfigOptions[];
    variants?: BaseVariant[];
    afterTabs?: React.ReactNode;
    beforeTabs?: React.ReactNode;
    beforeReviews?: React.ReactNode;
    bundleColor?: string[];
    sumBundlePrice?: any;
    isBundle?: boolean;
    quantity?: number;
    isRegularView?: boolean;
};

const tabs = {
    information: (description: string | null) => {
        return {
            id: 'Information',
            title: 'Information',
            content: <div>{description && <RichText content={description} />}</div>,
        };
    },
    customisation: (locations: ProductLocation[]) => {
        return {
            id: 'Customisation',
            title: 'Customisation',
            // language=HTML
            content: <ProductLocations locations={locations} />,
        };
    },
    delivery: () => {
        return {
            id: 'Delivery',
            title: 'Delivery',
            // language=HTML
            content: <DeliveryTab />,
        };
    },
};

type PreviousPriceContentProps = {
    previousPrices: PreviousPrice;
    discount: number;
    vat: boolean;
};

const PreviousPriceContent: React.FC<PreviousPriceContentProps> = ({ previousPrices, discount, vat }) => (
    <div className={classes.previousPrices}>
        <span className={classes.oldPrice}>
            <Price price={previousPrices.price_exl} priceIncl={previousPrices.price_inc} vat={vat} />
        </span>
        <div className={classes.discount}>-{Math.abs(discount)}%</div>
    </div>
);

export const ProductDetailLayout: React.FC<React.PropsWithChildren<Props>> = React.memo((props) => {
    const { isBrowser } = useRenderEnv();
    const { isMob, isDesk } = useBreakpointMatch();
    const { vat } = useVatCausesRender();
    const categoryId = props?.product?.categories?.[0]?.id || 1;
    const productTagIds = props.product.product_tags?.split(',');
    const { tagsAggregation } = useAggregationsByCategoryId(categoryId.toString());
    const productTags = productTagIds?.map((tagId) =>
        tagsAggregation.find((tag) => tag.value === tagId)?.label.toLowerCase(),
    );
    const isTaggedAsBestPrice = productTags?.includes('best price');

    const {
        isBundle,
        showSku,
        media_entries,
        price,
        bundleColor,
        sumBundlePrice,
        showTabs = true,
        quantity,
        isRegularView,
        productVideo,
    } = props;
    const [isOpenProductInfoModal, setIsOpenProductInfoModal] = useState(false);
    const openProductInfoModal = () => setIsOpenProductInfoModal(true);
    const closeProductInfoModal = () => setIsOpenProductInfoModal(false);

    const { data } = useQuery(categoryIdByName, {
        variables: {
            filters: {
                name: {
                    match: BESTSELLERS_CATEGORY,
                },
            },
        },
    });

    const { data: excludedCategory } = useQuery(categoryIdByName, {
        variables: {
            filters: {
                name: {
                    match: EXCLUDED_PRODUCTS_CATEGORY,
                },
            },
        },
    });

    const {
        sku,
        id,
        name,
        type,
        categories,
        description,
        locations,
        is_24_hour_delivery: is24HourDelivery,
        can_customise: canCustomise,
        brand_badge,
        size_guides,
        promo_banner,
        price_estimates,
        price_range,
        previous_prices,
        crosssell_products,
        upsell_products,
        brand,
        gender,
        small_image,
        url_key,
        default_category,
        default_category_url,
        breadcrumb_label,
        upsell_categories,
        more_from_brand,
    } = props.product;
    const hasConfigOptions = type === ProductType.ConfigurableProduct && props.options && props.variants;

    const onlyEmbroideryLocation = !locations.find((location) => location.method !== ApplicationMethod.Embroidery);
    const onlyPrintLocation = !locations.find((location) => location.method !== ApplicationMethod.Print);

    const specificTabs =
        locations.length > 0
            ? [tabs.information(description), tabs.customisation(locations), tabs.delivery()]
            : [tabs.information(description), tabs.delivery()];

    let brand_image = null;
    try {
        brand_image = require(`../../../static/img-brands/Logos/${getUrl(brand_badge)}`);
    } catch (err) {
        Bugsnag.notify(err as Error);
        brand_image = null;
    }

    const category = categories[0] || [];

    const isBundlePrice = Boolean(sumBundlePrice && sumBundlePrice.price !== 0 && sumBundlePrice.priceIncl !== 0);

    const computedPrices =
        (price?.final_price !== 0 && price?.final_price_incl !== 0) || (price?.price !== 0 && price?.price_incl !== 0)
            ? isBundlePrice
                ? sumBundlePrice
                : price
            : {
                  price: Number(price_range?.minimum_price?.final_price_excl_formatted),
                  price_incl: Number(price_range?.minimum_price?.final_price_formatted),
              };

    const showDiscount = Boolean(Number(previous_prices?.price_exl) && Number(previous_prices?.price_inc));
    const discount = showDiscount ? getPercentage(Number(previous_prices?.price_exl), computedPrices.price) : 0;

    const indexOfCategory = categories?.findIndex((category) =>
        CATEGORIES_TO_DISPLAY_DPD_AND_RETURNS_BADGES.find((displayedCategory) =>
            category.name.includes(displayedCategory),
        ),
    );
    const showDpdAndReturnsBadges = indexOfCategory! > -1;

    const productInfo = !isBundle && description ? <ProductInfo description={description} /> : null;

    return (
        <ProductGalleryProvider media_entries={media_entries} bundleColor={bundleColor}>
            <div className={classnames(classes.productDetail, { [classes.isBundle]: isBundle })}>
                <div
                    className={classnames(classes.productDetailLeftColumn, {
                        [classes.isBundle]: isBundle,
                    })}
                >
                    {!isBundle && (
                        <>
                            <ProductSidebars isMob={isMob}>
                                {computedPrices && locations.length > 0 && (
                                    <ProductSidebar>
                                        <CustomisationSidebar
                                            price={computedPrices}
                                            locations={locations}
                                            tieredPricing={(props.product as Product).margin_tier_prices}
                                            quantity={quantity}
                                            price_estimates={price_estimates}
                                            onlyEmbroideryLocation={onlyEmbroideryLocation}
                                            onlyPrintLocation={onlyPrintLocation}
                                        />
                                    </ProductSidebar>
                                )}
                                {size_guides && (
                                    <ProductSidebar>
                                        <SizeGuideLoader sizeGuide={size_guides} />
                                    </ProductSidebar>
                                )}
                                <ProductSidebar>
                                    <DeliveryGuideSidebar />
                                </ProductSidebar>
                            </ProductSidebars>
                            {isMob && (
                                <Breadcrumbs
                                    category={category.name}
                                    subcategory={name}
                                    categoryUrl={category.url_path || undefined}
                                    categoryBreadCrumbLabel={category.breadcrumb_label}
                                    indentation
                                    productSubCategory={
                                        default_category && default_category_url
                                            ? {
                                                  label: breadcrumb_label || default_category,
                                                  link: default_category_url,
                                              }
                                            : undefined
                                    }
                                />
                            )}
                        </>
                    )}
                    {isMob && isBundle && (
                        <div className={classnames(classes.productDetailTitle, { [classes.isBundle]: true })}>
                            {promo_banner && (
                                <div className={classes.productDetailPromo}>
                                    <RichText content={promo_banner} />
                                </div>
                            )}
                            <div
                                className={classnames(
                                    classes.productHeading,
                                    {
                                        [classes.isBundle]: isBundle,
                                    },
                                    {
                                        [classes.smallHeight]: iconsSmallWidth.includes(brand_badge ? brand_badge : ''),
                                    },
                                    {
                                        [classes.bigHeight]: iconsBiggerHeight.includes(brand_badge ? brand_badge : ''),
                                    },
                                    {
                                        [classes.mediumHeight]: iconsMediumHeight.includes(
                                            brand_badge ? brand_badge : '',
                                        ),
                                    },
                                    { [classes.notFitMaddins]: notFitMaddins === brand_badge },
                                    { [classes.notFitUneek]: notFitUneek === brand_badge },
                                    { [classes.notFitRegatta]: notFitRegatta === brand_badge },
                                    { [classes.notFitSnickers]: notFitSnickers === brand_badge },
                                    { [classes.notFitQuadra]: notFitQuadra === brand_badge },
                                    { [classes.notFitBlaklader]: notFitBlaklader === brand_badge },
                                    { [classes.notFitStanley]: notFitStanley === brand_badge },
                                    { [classes.notFitMagnum]: notFitMagnum === brand_badge },
                                    { [classes.notFitOxford]: notFitOxford === brand_badge },
                                    { [classes.notClubclass]: notClubclass === brand_badge },
                                    { [classes.notFitMilwaukee]: notFitMilwaukee === brand_badge },
                                    { [classes.notFitNutshell]: notFitNutshell === brand_badge },
                                    { [classes.notFitFallArrest]: notFitFallArrest === brand_badge },
                                    { [classes.notFitToolarrest]: notFitToolarrest === brand_badge },
                                    { [classes.notFitHomeAndLiving]: notFitHomeAndLiving === brand_badge },
                                )}
                            >
                                <div
                                    className={classnames(classes.productTitleAndBrandWrapper, {
                                        [classes.isBundle]: isBundle,
                                    })}
                                >
                                    {isBundle && (
                                        <h1 className={classnames(classes.title, { [classes.isBundle]: isBundle })}>
                                            {name}
                                        </h1>
                                    )}
                                    <Link to={`/brands/${brand_badge}`}>
                                        {brand_image ? (
                                            <div
                                                className={classnames(
                                                    classes.icon,
                                                    {
                                                        [classes.smallHeight]: iconsSmallWidth.includes(
                                                            brand_badge ? brand_badge : '',
                                                        ),
                                                    },
                                                    {
                                                        [classes.bigHeight]: iconsBiggerHeight.includes(
                                                            brand_badge ? brand_badge : '',
                                                        ),
                                                    },
                                                    {
                                                        [classes.mediumHeight]: iconsMediumHeight.includes(
                                                            brand_badge ? brand_badge : '',
                                                        ),
                                                    },
                                                    { [classes.notFitMaddins]: notFitMaddins === brand_badge },
                                                    { [classes.notFitUneek]: notFitUneek === brand_badge },
                                                    { [classes.notFitRegatta]: notFitRegatta === brand_badge },
                                                    { [classes.notFitSnickers]: notFitSnickers === brand_badge },
                                                    { [classes.notFitQuadra]: notFitQuadra === brand_badge },
                                                    { [classes.notFitBlaklader]: notFitBlaklader === brand_badge },
                                                    { [classes.notFitStanley]: notFitStanley === brand_badge },
                                                    { [classes.notFitMagnum]: notFitMagnum === brand_badge },
                                                    { [classes.notFitOxford]: notFitOxford === brand_badge },
                                                    { [classes.notFitBodyguard]: notFitBodyguard === brand_badge },
                                                    { [classes.notFitNutshell]: notFitNutshell === brand_badge },
                                                    { [classes.notFitFallArrest]: notFitFallArrest === brand_badge },
                                                    { [classes.notFitToolarrest]: notFitToolarrest === brand_badge },
                                                    {
                                                        [classes.notFitHomeAndLiving]:
                                                            notFitHomeAndLiving === brand_badge,
                                                    },
                                                    {
                                                        [classes.notFitFruitOfTheLoom]:
                                                            notFitFruitOfTheLoom === brand_badge,
                                                    },
                                                    {
                                                        [classes.bigIcons]: bigIcons.includes(
                                                            brand_badge ? brand_badge : '',
                                                        ),
                                                    },
                                                    {
                                                        [classes.notFitContinentalClothing]:
                                                            notFitContinentalClothing === brand_badge,
                                                    },
                                                    {
                                                        [classes.notFitV12]: notFitV12 === brand_badge,
                                                    },
                                                    {
                                                        [classes.notFitBAndC]: notFitBAndC === brand_badge,
                                                    },
                                                    {
                                                        [classes.notFitSupertouch]: notFitSupertouch === brand_badge,
                                                    },
                                                    {
                                                        [classes.notFit2786]: notFit2786 === brand_badge,
                                                    },
                                                    {
                                                        [classes.notFitCutterBuck]: notFitCutterBuck === brand_badge,
                                                    },
                                                )}
                                                style={{
                                                    backgroundImage: `url(${brand_image})`,
                                                    backgroundPosition: 'center',
                                                }}
                                            />
                                        ) : (
                                            <p className={classes.onlyText}>{brand_badge}</p>
                                        )}
                                    </Link>
                                    {!isBundle && <h1 className={classes.title}>{name}</h1>}
                                </div>
                                <div className={classes.skuAndRating}>
                                    {showSku && <p className={classes.productDetailSku}>Product Code: {sku}</p>}
                                    <YotpoRating urlKey={url_key || ''} productId={id} />
                                </div>
                            </div>
                            {computedPrices && (
                                <>
                                    {showDiscount && previous_prices && (
                                        <PreviousPriceContent
                                            previousPrices={previous_prices}
                                            discount={discount}
                                            vat={vat}
                                        />
                                    )}
                                    <small>{hasConfigOptions && 'From '}</small>
                                    <div
                                        className={classnames(classes.productDetailPriceAndBadge, {
                                            [classes.withDpdAndReturnsBadges]: showDpdAndReturnsBadges,
                                        })}
                                    >
                                        <ProductMainPrice
                                            isBundle={isBundle}
                                            price={computedPrices}
                                            bigPrice={showDiscount}
                                            showDpdAndReturnsBadges={showDpdAndReturnsBadges}
                                        />
                                        <PriceBadge
                                            isBundle={isBundle}
                                            price={computedPrices.price}
                                            showDpdAndReturnsBadges={showDpdAndReturnsBadges}
                                        />
                                    </div>
                                </>
                            )}
                            {props.priceRender && (
                                <>
                                    {props.priceRender}
                                    <PriceBadge isBundle={isBundle} />
                                </>
                            )}
                        </div>
                    )}
                    {isRegularView && (
                        <div className={classes.productDetailGallery}>
                            <ProductGallery
                                name={props.product.name}
                                deliveryIcon={is24HourDelivery}
                                canCustomise={canCustomise}
                                isBundle={isBundle}
                                productVideo={productVideo}
                            />
                        </div>
                    )}
                    {isMob && (
                        <div
                            className={classnames(classes.productDetailTitle, classes.productDetailMobile, {
                                [classes.isBundle]: false,
                            })}
                        >
                            <div
                                className={classnames(
                                    classes.productHeading,
                                    {
                                        [classes.isBundle]: isBundle,
                                    },
                                    {
                                        [classes.smallHeight]: iconsSmallWidth.includes(brand_badge ? brand_badge : ''),
                                    },
                                    {
                                        [classes.bigHeight]: iconsBiggerHeight.includes(brand_badge ? brand_badge : ''),
                                    },
                                    {
                                        [classes.mediumHeight]: iconsMediumHeight.includes(
                                            brand_badge ? brand_badge : '',
                                        ),
                                    },
                                    { [classes.notFitMaddins]: notFitMaddins === brand_badge },
                                    { [classes.notFitUneek]: notFitUneek === brand_badge },
                                    { [classes.notFitRegatta]: notFitRegatta === brand_badge },
                                    { [classes.notFitSnickers]: notFitSnickers === brand_badge },
                                    { [classes.notFitQuadra]: notFitQuadra === brand_badge },
                                    { [classes.notFitBlaklader]: notFitBlaklader === brand_badge },
                                    { [classes.notFitStanley]: notFitStanley === brand_badge },
                                    { [classes.notFitMagnum]: notFitMagnum === brand_badge },
                                    { [classes.notFitOxford]: notFitOxford === brand_badge },
                                    { [classes.notClubclass]: notClubclass === brand_badge },
                                    { [classes.notFitMilwaukee]: notFitMilwaukee === brand_badge },
                                    { [classes.notFitNutshell]: notFitNutshell === brand_badge },
                                    { [classes.notFitFallArrest]: notFitFallArrest === brand_badge },
                                    { [classes.notFitToolarrest]: notFitToolarrest === brand_badge },
                                    { [classes.notFitHomeAndLiving]: notFitHomeAndLiving === brand_badge },
                                )}
                            >
                                <div
                                    className={classnames(classes.productTitleAndBrandWrapper, {
                                        [classes.isBundle]: isBundle,
                                    })}
                                >
                                    {isBundle && (
                                        <h1 className={classnames(classes.title, { [classes.isBundle]: isBundle })}>
                                            {name}
                                        </h1>
                                    )}
                                    <Link to={`/brands/${brand_badge}`}>
                                        {brand_image ? (
                                            <div
                                                className={classnames(
                                                    classes.icon,
                                                    {
                                                        [classes.smallHeight]: iconsSmallWidth.includes(
                                                            brand_badge ? brand_badge : '',
                                                        ),
                                                    },
                                                    {
                                                        [classes.bigHeight]: iconsBiggerHeight.includes(
                                                            brand_badge ? brand_badge : '',
                                                        ),
                                                    },
                                                    {
                                                        [classes.mediumHeight]: iconsMediumHeight.includes(
                                                            brand_badge ? brand_badge : '',
                                                        ),
                                                    },
                                                    { [classes.notFitMaddins]: notFitMaddins === brand_badge },
                                                    { [classes.notFitUneek]: notFitUneek === brand_badge },
                                                    { [classes.notFitRegatta]: notFitRegatta === brand_badge },
                                                    { [classes.notFitSnickers]: notFitSnickers === brand_badge },
                                                    { [classes.notFitQuadra]: notFitQuadra === brand_badge },
                                                    { [classes.notFitBlaklader]: notFitBlaklader === brand_badge },
                                                    { [classes.notFitStanley]: notFitStanley === brand_badge },
                                                    { [classes.notFitMagnum]: notFitMagnum === brand_badge },
                                                    { [classes.notFitMilwaukee]: notFitMilwaukee === brand_badge },
                                                    { [classes.notFitNutshell]: notFitNutshell === brand_badge },
                                                    { [classes.notFitFallArrest]: notFitFallArrest === brand_badge },
                                                    { [classes.notFitToolarrest]: notFitToolarrest === brand_badge },
                                                    {
                                                        [classes.notFitHomeAndLiving]:
                                                            notFitHomeAndLiving === brand_badge,
                                                    },
                                                    { [classes.notFitOxford]: notFitOxford === brand_badge },
                                                    {
                                                        [classes.notFitHellyHansen]: notFitHellyHansen === brand_badge,
                                                    },
                                                    { [classes.notFitBodyguard]: notFitBodyguard === brand_badge },
                                                    { [classes.notFitNimbus]: notFitNimbus === brand_badge },
                                                    { [classes.notFitOnna]: notFitOnna === brand_badge },
                                                    {
                                                        [classes.notFitFruitOfTheLoom]:
                                                            notFitFruitOfTheLoom === brand_badge,
                                                    },
                                                    {
                                                        [classes.bigIcons]: bigIcons.includes(
                                                            brand_badge ? brand_badge : '',
                                                        ),
                                                    },
                                                    {
                                                        [classes.notFitContinentalClothing]:
                                                            notFitContinentalClothing === brand_badge,
                                                    },
                                                    {
                                                        [classes.notFitV12]: notFitV12 === brand_badge,
                                                    },
                                                    { [classes.notFitBAndC]: notFitBAndC === brand_badge },
                                                    { [classes.notFitOgio]: notFitOgio === brand_badge },
                                                    { [classes.notFitSupertouch]: notFitSupertouch === brand_badge },
                                                    { [classes.notFit2786]: notFit2786 === brand_badge },
                                                    { [classes.notFitCutterBuck]: notFitCutterBuck === brand_badge },
                                                )}
                                                style={{
                                                    backgroundImage: `url(${brand_image})`,
                                                    backgroundPosition: 'center',
                                                }}
                                            />
                                        ) : (
                                            <p className={classes.onlyText}>{brand_badge}</p>
                                        )}
                                    </Link>
                                    {!isBundle && <h1 className={classes.title}>{name}</h1>}
                                </div>
                                <div className={classes.skuAndRating}>
                                    {showSku && <p className={classes.productDetailSku}>Product Code: {sku}</p>}
                                    <YotpoRating urlKey={url_key || ''} productId={id} />
                                </div>
                            </div>
                            {computedPrices && (
                                <>
                                    {showDiscount && previous_prices && (
                                        <PreviousPriceContent
                                            previousPrices={previous_prices}
                                            discount={discount}
                                            vat={vat}
                                        />
                                    )}
                                    <small>{hasConfigOptions && 'From '}</small>
                                    <div
                                        className={classnames(classes.productDetailPriceAndBadge, {
                                            [classes.withDpdAndReturnsBadges]: showDpdAndReturnsBadges,
                                        })}
                                    >
                                        <ProductMainPrice
                                            isBundle={isBundle}
                                            price={computedPrices}
                                            bigPrice={showDiscount}
                                            showDpdAndReturnsBadges={showDpdAndReturnsBadges}
                                        />
                                        <PriceBadge
                                            isBundle={isBundle}
                                            price={computedPrices.price}
                                            showDpdAndReturnsBadges={showDpdAndReturnsBadges}
                                            showBestPrice={isTaggedAsBestPrice}
                                        />
                                    </div>
                                </>
                            )}
                            {props.priceRender && (
                                <>
                                    {props.priceRender}
                                    <PriceBadge isBundle={isBundle} />
                                </>
                            )}
                        </div>
                    )}
                    {isMob && (
                        <div className={classnames(classes.productDetailOptions, { [classes.isBundle]: isBundle })}>
                            {props.children}
                        </div>
                    )}
                    <div
                        className={classnames(classes.productDetailInformation, {
                            [classes.isBundle]: isBundle,
                        })}
                    >
                        {isDesk && (
                            <>
                                {productInfo}
                                {!isRegularView && (
                                    <>
                                        <button
                                            className={classes.openInfoButton}
                                            onClick={openProductInfoModal}
                                            type="button"
                                            aria-haspopup="dialog"
                                        >
                                            Open info
                                        </button>
                                        <ModalProductInfo
                                            isOpenProductInfoModal={isOpenProductInfoModal}
                                            closeProductInfoModal={closeProductInfoModal}
                                            description={props.product.description || ''}
                                            is24HourDelivery={props.product.is_24_hour_delivery}
                                            canCustomise={props.product.can_customise}
                                            mediaEntires={props.product.media_entries}
                                            productName={props.product.name}
                                            delivery={<DeliveryTab />}
                                        />
                                    </>
                                )}
                            </>
                        )}
                        {isBundle && showTabs && (
                            <>
                                {props.beforeTabs}
                                <Tabs strategy="mount" collapseAll={isBrowser} tabs={specificTabs} />
                                {props.afterTabs}
                                <QuickQuote sku={sku} isBundle={isBundle} />
                            </>
                        )}
                        {isMob && productInfo}
                    </div>
                    <YotpoReviews
                        urlKey={url_key || ''}
                        productId={id}
                        price={computedPrices.price}
                        name={name}
                        image={small_image}
                    />

                    {categories.length > 0 && (
                        <ProductDetailRelatedProducts
                            fromBrand={{
                                gender: gender,
                                brandId: brand,
                                productId: id,
                                brandName: brand_badge,
                            }}
                            moreFromBrand={more_from_brand}
                            additionalCategoryId={data?.categoryIdByName?.[0]?.id || undefined}
                            excludedProductsCategoryId={excludedCategory?.categoryIdByName?.[0]?.id || undefined}
                            crosssellProducts={
                                camelcaseKeys((crosssell_products ?? []) as any, {
                                    deep: true,
                                }) as unknown as ProductItemBySku[]
                            }
                            upsellProducts={
                                camelcaseKeys((upsell_products ?? []) as any, {
                                    deep: true,
                                }) as unknown as ProductItemBySku[]
                            }
                        />
                    )}
                </div>

                {isDesk && (
                    <div
                        className={classnames(classes.productDetailRightColumn, {
                            [classes.isBundle]: isBundle,
                        })}
                    >
                        {!isBundle && (
                            <Breadcrumbs
                                category={category.name}
                                subcategory={name}
                                categoryUrl={category.url_path || undefined}
                                categoryBreadCrumbLabel={category.breadcrumb_label}
                                indentation
                                productSubCategory={
                                    default_category && default_category_url
                                        ? { label: breadcrumb_label || default_category, link: default_category_url }
                                        : undefined
                                }
                            />
                        )}
                        <div className={classnames(classes.productDetailTitle, { [classes.isBundle]: isBundle })}>
                            {isBundle && promo_banner && (
                                <div className={classes.productDetailPromo}>
                                    <RichText content={promo_banner} />
                                </div>
                            )}
                            <div
                                className={classnames(
                                    classes.productHeading,
                                    {
                                        [classes.isBundle]: isBundle,
                                    },
                                    {
                                        [classes.smallHeight]: iconsSmallWidth.includes(brand_badge ? brand_badge : ''),
                                    },
                                    {
                                        [classes.bigHeight]: iconsBiggerHeight.includes(brand_badge ? brand_badge : ''),
                                    },
                                    {
                                        [classes.mediumHeight]: iconsMediumHeight.includes(
                                            brand_badge ? brand_badge : '',
                                        ),
                                    },
                                    { [classes.notFitMaddins]: notFitMaddins === brand_badge },
                                    { [classes.notFitUneek]: notFitUneek === brand_badge },
                                    { [classes.notFitRegatta]: notFitRegatta === brand_badge },
                                    { [classes.notFitSnickers]: notFitSnickers === brand_badge },
                                    { [classes.notFitQuadra]: notFitQuadra === brand_badge },
                                    { [classes.notFitBlaklader]: notFitBlaklader === brand_badge },
                                    { [classes.notFitStanley]: notFitStanley === brand_badge },
                                    { [classes.notFitMagnum]: notFitMagnum === brand_badge },
                                    { [classes.notFitOxford]: notFitOxford === brand_badge },
                                    { [classes.notClubclass]: notClubclass === brand_badge },
                                    { [classes.notFitMilwaukee]: notFitMilwaukee === brand_badge },
                                    { [classes.notFitNutshell]: notFitNutshell === brand_badge },
                                    { [classes.notFitFallArrest]: notFitFallArrest === brand_badge },
                                    { [classes.notFitToolarrest]: notFitToolarrest === brand_badge },
                                    { [classes.notFitHomeAndLiving]: notFitHomeAndLiving === brand_badge },
                                )}
                            >
                                <div
                                    className={classnames(classes.productTitleAndBrandWrapper, {
                                        [classes.isBundle]: isBundle,
                                    })}
                                >
                                    {isBundle && (
                                        <h1 className={classnames(classes.title, { [classes.isBundle]: isBundle })}>
                                            {name}
                                        </h1>
                                    )}
                                    <Link to={`/brands/${brand_badge}`}>
                                        {brand_image ? (
                                            <div
                                                className={classnames(
                                                    classes.icon,
                                                    {
                                                        [classes.smallHeight]: iconsSmallWidth.includes(
                                                            brand_badge ? brand_badge : '',
                                                        ),
                                                    },
                                                    {
                                                        [classes.bigHeight]: iconsBiggerHeight.includes(
                                                            brand_badge ? brand_badge : '',
                                                        ),
                                                    },
                                                    {
                                                        [classes.mediumHeight]: iconsMediumHeight.includes(
                                                            brand_badge ? brand_badge : '',
                                                        ),
                                                    },
                                                    { [classes.notFitMaddins]: notFitMaddins === brand_badge },
                                                    { [classes.notFitUneek]: notFitUneek === brand_badge },
                                                    { [classes.notFitRegatta]: notFitRegatta === brand_badge },
                                                    { [classes.notFitSnickers]: notFitSnickers === brand_badge },
                                                    { [classes.notFitQuadra]: notFitQuadra === brand_badge },
                                                    { [classes.notFitBlaklader]: notFitBlaklader === brand_badge },
                                                    { [classes.notFitStanley]: notFitStanley === brand_badge },
                                                    { [classes.notFitMagnum]: notFitMagnum === brand_badge },
                                                    { [classes.notFitMilwaukee]: notFitMilwaukee === brand_badge },
                                                    { [classes.notFitOxford]: notFitOxford === brand_badge },
                                                    { [classes.notFitBodyguard]: notFitBodyguard === brand_badge },
                                                    { [classes.notFitNimbus]: notFitNimbus === brand_badge },
                                                    { [classes.notFitOnna]: notFitOnna === brand_badge },
                                                    { [classes.notFitNutshell]: notFitNutshell === brand_badge },
                                                    { [classes.notFitFallArrest]: notFitFallArrest === brand_badge },
                                                    { [classes.notFitToolarrest]: notFitToolarrest === brand_badge },
                                                    {
                                                        [classes.notFitFruitOfTheLoom]:
                                                            notFitFruitOfTheLoom === brand_badge,
                                                    },
                                                    {
                                                        [classes.notFitHomeAndLiving]:
                                                            notFitHomeAndLiving === brand_badge,
                                                    },
                                                    {
                                                        [classes.bigIcons]: bigIcons.includes(
                                                            brand_badge ? brand_badge : '',
                                                        ),
                                                    },
                                                    {
                                                        [classes.notFitContinentalClothing]:
                                                            notFitContinentalClothing === brand_badge,
                                                    },
                                                    {
                                                        [classes.notFitV12]: notFitV12 === brand_badge,
                                                    },
                                                    { [classes.notFitBAndC]: notFitBAndC === brand_badge },
                                                    { [classes.notFitOgio]: notFitOgio === brand_badge },
                                                    {
                                                        [classes.notFitSupertouch]: notFitSupertouch === brand_badge,
                                                    },
                                                    {
                                                        [classes.notFit2786]: notFit2786 === brand_badge,
                                                    },
                                                    {
                                                        [classes.notFitCutterBuck]: notFitCutterBuck === brand_badge,
                                                    },
                                                )}
                                                style={{
                                                    backgroundImage: `url(${brand_image})`,
                                                    backgroundPosition: 'center',
                                                }}
                                            />
                                        ) : (
                                            <p className={classes.onlyText}>{brand_badge}</p>
                                        )}
                                    </Link>
                                    {!isBundle && <h1 className={classes.title}>{name}</h1>}
                                </div>
                                <div className={classes.skuAndRating}>
                                    {showSku && <p className={classes.productDetailSku}>Product Code: {sku}</p>}
                                    <YotpoRating urlKey={url_key || ''} productId={id} />
                                </div>
                            </div>
                            {computedPrices && (
                                <>
                                    {showDiscount && previous_prices && (
                                        <PreviousPriceContent
                                            previousPrices={previous_prices}
                                            discount={discount}
                                            vat={vat}
                                        />
                                    )}
                                    <small>{hasConfigOptions && 'From '}</small>
                                    <div
                                        className={classnames(classes.productDetailPriceAndBadge, {
                                            [classes.withDpdAndReturnsBadges]: showDpdAndReturnsBadges,
                                        })}
                                    >
                                        <ProductMainPrice
                                            isBundle={isBundle}
                                            price={computedPrices}
                                            bigPrice={showDiscount}
                                            showDpdAndReturnsBadges={showDpdAndReturnsBadges}
                                        />
                                        <PriceBadge
                                            isBundle={isBundle}
                                            price={computedPrices.price}
                                            showDpdAndReturnsBadges={showDpdAndReturnsBadges}
                                            showBestPrice={isTaggedAsBestPrice}
                                        />
                                    </div>
                                </>
                            )}
                            {props.priceRender && (
                                <>
                                    {props.priceRender}
                                    <PriceBadge isBundle={isBundle} />
                                </>
                            )}
                        </div>
                        <div className={classnames(classes.productDetailOptions, { [classes.isBundle]: isBundle })}>
                            {props.children}
                        </div>
                    </div>
                )}

                {props.beforeReviews && <div className={classes.productDetailBeforeReviews}>{props.beforeReviews}</div>}
                <RelatedCategories categories={upsell_categories} />
            </div>
        </ProductGalleryProvider>
    );
});

export const ProductMainPrice: React.FC<{
    price: ProductPrice;
    isBundle: boolean | undefined;
    bigPrice?: boolean;
    showDpdAndReturnsBadges?: boolean;
}> = ({ price, isBundle, showDpdAndReturnsBadges }) => {
    const { vat } = useVatCausesRender();
    const isIrishUser = useIsIrishUser();
    const showFreeDelivery = !isBundle && price.price >= MIN_PRICE_TO_FREE_FEDEX_DELIVERY && !isIrishUser;

    if (price.price === -1 && price.price_incl === -1) {
        return (
            <div
                className={classnames(classes.productDetailPriceAndBadge, {
                    [classes.withDpdAndReturnsBadges]: showDpdAndReturnsBadges,
                })}
            >
                <span
                    className={classnames(classes.productDetailPrice, {
                        [classes.isBundle]: isBundle,
                    })}
                >
                    <StyledText>...</StyledText>
                </span>
                <PriceBadge isBundle={isBundle} price={price.price} showDpdAndReturnsBadges={showDpdAndReturnsBadges} />
            </div>
        );
    }

    return (
        <div className={classes.productMainPriceWrapper}>
            <Price
                price={price.price}
                priceIncl={price.price_incl}
                vat={vat}
                className={classnames(classes.productDetailPrice, {
                    [classes.isBundle]: isBundle,
                })}
            />
            {showFreeDelivery && (
                <span className={classnames(classes.freeDeliveryLine, classes.freeDeliveryLink)}>
                    <img
                        className={classes.freeDeliveryCheck}
                        src={require('src/components/Svg/svgs/check-green.svg')}
                        alt="Check icon"
                    />
                    Free Delivery
                    <img src="../../../static/icons/fedex.svg" alt="FedEx" className={classes.freeDeliveryLogo} />
                </span>
            )}
        </div>
    );
};

const iconsMediumHeight: string[] = ['asquith-fox', 'awdis', 'build-your-brand', 'kimood', 'workwear-express'];
const iconsBiggerHeight: string[] = [
    'amblers',
    'orange-standard',
    'russell',
    'continental-clothing',
    'neoblu',
    'orn',
    'skinni-fit',
    'carhartt',
    'unbreakable',
    'mcr',
    'colortone',
];
const bigIcons = [
    '3m',
    'adidas',
    'as-colour',
    'behrens',
    'callaway',
    'centurion',
    'craghoppers',
    'dickies',
    'dickies-medical',
    'disley',
    'drager',
    'dupont',
    'finden-hales',
    'gvs',
    'hard-yakka',
    'himalayan',
    'jcb',
    'jsp',
    'kimood',
    'leo-workwear',
    'nike',
    'the-north-face',
    'polyco',
    'premier',
    'proforce',
    'puma',
    'pyramex',
    'safety-jogger',
    'sols',
    'timberland-pro',
    'tombo',
    'towel-city',
    'tri-dri',
    'under-armour',
    'red-wing-safety',
    'tusker',
    'new-balance',
    'bunzl',
    'cutter-buck',
];
const notFitMaddins = 'maddins';
const notFitUneek = 'uneek';
const notFitRegatta = 'regatta';
const notFitSnickers = 'snickers';
const notFitQuadra = 'quadra';
const notFitBlaklader = 'blaklader';
const notFitStanley = 'stanley-workwear';
const notFitMagnum = 'magnum';
const notFitHellyHansen = 'helly-hansen';
const notFitBodyguard = 'bodyguard-workwear';
const notFitNimbus = 'nimbus';
const notFitOnna = 'onna-by-premier';
const notFitFruitOfTheLoom = 'fruit-of-the-loom';
const notClubclass = 'clubclass';
const notFitMilwaukee = 'milwaukee';
const notFitOxford = 'oxford';
const notFitNutshell = 'nutshell';
const notFitFallArrest = 'fall-arrest';
const notFitToolarrest = 'tool-arrest';
const notFitHomeAndLiving = 'home-living';
const notFitContinentalClothing = 'continental-clothing';
const notFitV12 = 'v12-footwear';
const notFitBAndC = 'b-c-collection';
const notFitOgio = 'ogio';
const notFitSupertouch = 'supertouch';
const notFit2786 = '2786';
const notFitCutterBuck = 'cutter-buck';
